import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { ProgramType } from "@/modules/programs/types/programTypes"
import logoIncrustation from "@images/logo_incrustation.png"
import { ProSmallIcon } from "@images/svgIcons/ProSmallIcon"
import { InfinityIcon as InfinityCustomIcon } from "@images/svgIcons/infinityIcon"
import { OnOffIcon } from "@images/svgIcons/onOffIcon"
import { word } from "@modules/core/utils/i18n"
import { useProgramSidebar } from "@modules/programs/hooks/useProgramSidebar"
import { Check, PlusIcon } from "lucide-react"
import type { ReactNode } from "react"

export function CoachOfferRemoteTypeSelectionView() {
  const { navigateToProgramCreation } = useProgramSidebar()

  return (
    <div className="flex flex-col gap-16 px-5 pt-12 pb-8">
      <Text fontStyle="TL">{word("coach.offers.form.type.selection.remote.title")}</Text>

      <div className="flex flex-col gap-2">
        <Text fontStyle="TS" className="text-textOnSurface-default">
          {word("coach.offers.form.remote.description")}
        </Text>
        <div className="grid grid-cols-2 gap-4">
          {getTypeCards().map((card) => (
            <RemoteTypeCard key={card.type} {...card} onSelectType={navigateToProgramCreation} />
          ))}
        </div>
      </div>
    </div>
  )
}

type RemoteTypeCardInfo = {
  type: ProgramType
  icon: ReactNode
  advantages: string[]
}

type RemoteTypeCardProps = RemoteTypeCardInfo & {
  onSelectType: (type: ProgramType) => void
} & React.ComponentProps<"div">

function RemoteTypeCard({ type, icon, advantages, onSelectType, className, ...props }: RemoteTypeCardProps) {
  return (
    <div
      className={cn(
        "relative flex h-full flex-col gap-4 overflow-hidden rounded-2xl bg-surface-background-secondary p-4",
        className,
      )}
      {...props}
    >
      <img
        src={logoIncrustation}
        alt=""
        aria-hidden
        className="pointer-events-none absolute right-0 bottom-0 w-3/4 opacity-10 invert select-none"
      />
      <div className="flex flex-row items-center gap-2">
        {icon}
        <Text fontStyle="TM" className="font-bold text-textOnSurface-default">
          {getTypeLabel(type)}
        </Text>
      </div>

      <div className="flex flex-col gap-3">
        <Text fontStyle="LS" className="text-textOnSurface-subtle">
          {word("coach.offers.form.advantages.subtitle")}
        </Text>

        {advantages.map((advantage, index) => (
          <div key={index} className="flex w-full items-start gap-2">
            <Check size={20} className="mt-0.5 shrink-0 text-textSuccess" />
            <Text fontStyle="LM" className="text-textOnSurface-default">
              {advantage}
            </Text>
          </div>
        ))}
      </div>

      <Button
        variant="primary"
        onClick={() => onSelectType(type)}
        className="mt-auto flex w-full flex-row items-center justify-center gap-2"
      >
        <PlusIcon size={16} />
        {word("global.create")}
      </Button>
    </div>
  )
}

function getTypeCards(): RemoteTypeCardInfo[] {
  return [
    {
      type: ProgramType.VIRTUAL,
      icon: <InfinityCustomIcon color="currentColor" size={24} width={24} height={24} />,
      advantages: [
        word("coach.offers.form.type.continuous.advantage1"),
        word("coach.offers.form.type.continuous.advantage2"),
        word("coach.offers.form.type.continuous.advantage3"),
      ],
    },
    {
      type: ProgramType.ONOFF,
      icon: <OnOffIcon color="currentColor" size={24} width={24} height={24} />,
      advantages: [
        word("coach.offers.form.type.onOff.advantage1"),
        word("coach.offers.form.type.onOff.advantage2"),
        word("coach.offers.form.type.onOff.advantage3"),
      ],
    },
    {
      type: ProgramType.PRO,
      icon: <ProSmallIcon color="currentColor" size={24} width={24} height={24} />,
      advantages: [
        word("coach.offers.form.type.pro.advantage1"),
        word("coach.offers.form.type.pro.advantage2"),
        word("coach.offers.form.type.pro.advantage3"),
      ],
    },
  ]
}

function getTypeLabel(type: ProgramType): string {
  switch (type) {
    case ProgramType.VIRTUAL:
      return word("coach.offers.type.continuousPrograms")
    case ProgramType.ONOFF:
      return word("coach.offers.type.onOffPrograms")
    case ProgramType.PRO:
      return word("coach.offers.type.proPrograms")
    default:
      return ""
  }
}
